import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CHLookupEvaluate_Result } from '../../../type-definitions/checklist/responses/checklist';
import { UtilityService } from '../../../core/services/utility.service';
import { ApiResponse } from '../../../shared/model/api-response.model';

@Injectable({
  providedIn: 'root',
})
export class ChecklistService {
  private http = inject(HttpClient);
  private utilityService = inject(UtilityService);

  getAttachmentTypes() {
    const url = '/Checklist/GetAttachmentTypes';
    return this.http.get(url).toPromise();
  }

  getItemGroups(checklistTemplateID?: number) {
    const url = '/Checklist/GetItemGroups?checklistTemplateID=' + checklistTemplateID;
    return this.http.get(url).toPromise();
  }

  getChecklistTemplates(checklistTemplateCatgID?: number) {
    const url = '/Checklist/GetChecklistTemplates?checklistTemplateCatgID=' + checklistTemplateCatgID;
    return this.http.get(url).toPromise();
  }

  toggleTemplateActive(checklistTemplateID: number, isActive: boolean) {
    return this.http
      .post('/Checklist/ToggleTemplateActive', {
        checklistTemplateID: checklistTemplateID,
        isActive: isActive,
      })
      .toPromise();
  }

  addUpdateTemplate(requestObj: any) {
    return this.http
      .post('/Checklist/AddUpdateTemplate', {
        checklistTemplateID: requestObj.checklistTemplateID,
        checklistTemplateCatgID: requestObj.checklistTemplateCatgID,
        checklistTemplateName: requestObj.checklistTemplateName,
        userGroupID: requestObj.userGroupID,
        reportRDLFileName: requestObj.reportRDLFileName,
        checklistTextHeader: requestObj.checklistTextHeader,
        workflowInclPM: requestObj.workflowInclPM,
        workflowInclJobRev: requestObj.workflowInclJobRev,
        workflowInclInvRev: requestObj.workflowInclInvRev,
        workflowInclUserGroupID: requestObj.workflowInclUserGroupID,
        workflowInclTimecardManager: requestObj.workflowInclTimecardManager,
        enableWorkflow: requestObj.enableWorkflow,
        adminGroupID: requestObj.adminGroupID,
        disableSubmission: requestObj.disableSubmission,
        attachmentType: requestObj.attachmentType,
        rdlData: requestObj.rdlData,
        attachDocumentFlagValue: requestObj.attachDocumentFlagValue,
        workflowUsersCanFinalize: requestObj.WorkflowUsersCanFinalize,
      })
      .toPromise();
  }

  copyChecklistTemplate(origChecklistTemplateID: number, newChecklistTemplateName: string) {
    return this.http
      .post('/Checklist/CopyChecklistTemplate', {
        origChecklistTemplateID: origChecklistTemplateID,
        newChecklistTemplateName: newChecklistTemplateName,
      })
      .toPromise();
  }

  templateItemToggleActive(checklistTemplateItemID: number, isActive: boolean) {
    return this.http
      .post('/Checklist/TemplateItemToggleActive', {
        checklistTemplateItemID: checklistTemplateItemID,
        isActive: isActive,
      })
      .toPromise();
  }

  addUpdateTemplateItem(requestObj: any) {
    return this.http
      .post('/Checklist/AddUpdateTemplateItem', {
        checklistTemplateItemID: requestObj.checklistTemplateItemID,
        checklistTemplateItemGrpID: requestObj.checklistTemplateItemGrpID,
        responseTypeID: requestObj.responseTypeID,
        itemPrompt: requestObj.itemPrompt,
        itemNumber: requestObj.itemNumber,
        itemLongPrompt: requestObj.itemLongPrompt,
        checklistLookupID: requestObj.checklistLookupID,
        maxResponseLength: requestObj.maxResponseLength,
        validationRegex: requestObj.validationRegex,
        personalInfoField: requestObj.personalInfoField,
        isAdminField: requestObj.isAdminField,
        isRequiredAttachment: requestObj.isRequiredAttachment,
        rebindDropdown: requestObj.rebindDropdown,
        showComments: requestObj.showComments,
        showAttachments: requestObj.showAttachments,
        isRequired: requestObj.isRequired,
        showManagerComments: requestObj.showManagerComments,
        isManagerCommentsRequired: requestObj.isManagerCommentsRequired,
        serviceItemField: requestObj.serviceItemField,
      })
      .toPromise();
  }

  getItem(checklistInstanceID) {
    const url = '/Checklist/GetItem?checklistInstanceID=' + checklistInstanceID;
    return this.http.get(url).toPromise();
  }

  getItemsByTemplateID(templateId: number) {
    const url = '/Checklist/GetItemsByTemplateID?checklistTemplateID=' + templateId;
    return this.http.get(url).toPromise();
  }

  getLookupDataTypes(mode: string) {
    const url = '/Checklist/GetLookupDataTypes';
    const params = new HttpParams().set('mode', mode);
    return this.http.get(url, { params: params }).toPromise();
  }

  getTemplateInfo(checklistTemplateID: number) {
    const url = '/Checklist/GetTemplateInfo?checklistTemplateID=' + checklistTemplateID;
    return this.http.get(url).toPromise();
  }

  getTemplateInfoFromInstance(checklistInstanceID: number) {
    const url = '/Checklist/GetTemplateInfoFromInstance?checklistInstanceID=' + checklistInstanceID;
    return this.http.get(url).toPromise();
  }

  getHeaderFieldsByTemplateID(checklistTemplateID: number) {
    const url = '/Checklist/GetHeaderFieldsByTemplateID?checklistTemplateID=' + checklistTemplateID;
    return this.http.get(url).toPromise();
  }

  insertUpdateHeaderFields(requestObj: any) {
    return this.http
      .post('/Checklist/InsertUpdateHeaderFields', {
        checklistHeaderFieldID: requestObj.checklistHeaderFieldID,
        checklistTemplateID: requestObj.checklistTemplateID,
        headerFieldName: requestObj.headerFieldName,
        headerFieldLabel: requestObj.headerFieldLabel,
        fieldType: requestObj.fieldType,
        fieldIsRequired: requestObj.fieldIsRequired,
        isRestrictedAccess: requestObj.isRestrictedAccess,
        isActive: requestObj.isActive,
      })
      .toPromise();
  }

  addItemGroups(checklistTemplateID: number, itemGroupName: string, itemGroupSeq?: number, itemGroupPct?: number) {
    return this.http
      .post('/Checklist/AddItemGroups', {
        checklistTemplateID: checklistTemplateID,
        itemGroupName: itemGroupName,
        itemGroupSeq: itemGroupSeq,
        itemGroupPct: itemGroupPct,
      })
      .toPromise();
  }

  editItemGroups(checklistTemplateID: number, itemGroupName: string, itemGroupSeq?: number, itemGroupPct?: number) {
    return this.http
      .post('/Checklist/EditItemGroups', {
        checklistTemplateID: checklistTemplateID,
        itemGroupName: itemGroupName,
        itemGroupSeq: itemGroupSeq,
        itemGroupPct: itemGroupPct,
      })
      .toPromise();
  }

  moveChecklistGroup(direction?: number, checklistTemplateItemGrpID?: number) {
    return this.http
      .post('/Checklist/MoveChecklistGroup', {
        direction: direction,
        checklistTemplateItemGrpID: checklistTemplateItemGrpID,
      })
      .toPromise();
  }

  submitChecklistOrder(direction?: number, checklistInstanceID?: number) {
    return this.http
      .post('/Checklist/SubmitChecklistOrder', {
        direction: direction,
        checklistInstanceID: checklistInstanceID,
      })
      .toPromise();
  }

  deleteItemGroup(checklistTemplateItemGrpID: number) {
    return this.http
      .post('/Checklist/DeleteItemGroup', {
        checklistTemplateItemGrpID: checklistTemplateItemGrpID,
      })
      .toPromise();
  }

  getTemplateCategories(checklistTemplateCatgID?: number) {
    const url = '/Checklist/GetTemplateCategories?checklistTemplateCatgID=' + checklistTemplateCatgID;
    return this.http.get(url).toPromise();
  }

  getChecklistHistory(formData: any, checklistTemplateCatgID: number) {
    const url = checklistTemplateCatgID == 1 ? '/Checklist/GetChecklistHistory' : '/Checklist/GetActionItemsHistory';
    let httpParams = new HttpParams()
      .set('fromDateString', this.utilityService.getISODate(formData.fromDate))
      .set('toDateString', this.utilityService.getISODate(formData.toDate));
    if (formData.selectedJobPrefix !== null && formData.selectedJobPrefix !== '') {
      httpParams = httpParams.set('jobPrefix', formData.selectedJobPrefix);
    }
    return this.http.get(url, { params: httpParams }).toPromise();
  }

  getChecklistReviewers(checklistInstanceID: number) {
    return this.http.get('/Checklist/GetChecklistReviewers?checklistInstanceID=' + checklistInstanceID).toPromise();
  }

  initializeChecklistReviewers(checklistInstanceID: number) {
    return this.http
      .post('/Checklist/InitializeChecklistReviewers', {
        checklistInstanceID: checklistInstanceID,
      })
      .toPromise();
  }

  deleteChecklistReviewers(checklistRevID: number) {
    return this.http
      .post('/Checklist/DeleteChecklistReviewers', {
        checklistRevID: checklistRevID,
      })
      .toPromise();
  }

  addChecklistReviewer(userKeyID: number, checklistInstanceID: any, reviewerSource: string) {
    return this.http
      .post('/Checklist/AddChecklistReviewer', {
        userKeyID: userKeyID,
        checklistInstanceID: checklistInstanceID,
        reviewerSource: reviewerSource,
      })
      .toPromise();
  }

  sendChecklists(
    checklistInstanceIDs: number[],
    filenames: string[],
    fromEmail: string,
    toEmail: string[],
    subject: string,
    message: string,
    newFilenames: string[],
    newFiles: any[],
    templateID: any[],
    mailFrom: string,
    queueType: string,
    notificationMode: string,
    sMSTo: string,
    checklistTemplateCatgID: number
  ) {
    return this.http
      .post('/Checklist/SendChecklists', {
        checklistInstanceIDs: checklistInstanceIDs,
        filenames: filenames,
        fromEmail: fromEmail,
        toEmail: toEmail,
        subject: subject,
        message: message,
        newFilenames: newFilenames,
        newFiles: newFiles,
        templateID: templateID,
        mailFrom: mailFrom,
        queueType: queueType,
        notificationMode: notificationMode,
        sMSTo: sMSTo,
        checklistTemplateCatgID: checklistTemplateCatgID,
      })
      .toPromise();
  }

  sendChecklistSign(
    signatureDataStr: string,
    checklistInstanceID: number,
    typeSign: boolean,
    latitude: number,
    longitude: number,
    pdfFileData: string = null,
    pdfFileName: string = null
  ) {
    return this.http
      .post('/Checklist/sendChecklistSign', {
        signatureDataStr: signatureDataStr,
        checklistInstanceID: checklistInstanceID,
        typeSign: typeSign,
        latitude: latitude,
        longitude: longitude,
        pdfFileData: pdfFileData,
        pdfFileName: pdfFileName,
      })
      .toPromise();
  }

  getChecklistInstanceDetails(checklistInstanceID: number) {
    const url = '/Checklist/GetChecklistInstanceDetails?checklistInstanceID=' + checklistInstanceID;
    return this.http.get(url).toPromise();
  }

  getInfoByOnboardID() {
    const url = '/Hiring/GetInfoByOnboardID';
    return this.http.get(url).toPromise();
  }

  getHeaderFields(checklistInstanceID: number) {
    const url = '/Checklist/GetHeaderFields?checklistInstanceID=' + checklistInstanceID;
    return this.http.get(url).toPromise();
  }

  getChoicesByHeaderFieldID(checklistHeaderFieldID: number, checklistInstanceID: number, pRCO?: number, workOrderScopeID?: number) {
    const url = '/Checklist/GetChoicesByHeaderFieldID';
    const httpParams = new HttpParams()
      .set('checklistHeaderFieldID', checklistHeaderFieldID)
      .set('checklistInstanceID', checklistInstanceID)
      .set('pRCO', pRCO)
      .set('workOrderScopeID', workOrderScopeID);
    return this.http.get(url, { params: httpParams }).toPromise();
  }

  updateHeaderFieldValue(checklistInstanceID: number, checklistHeaderFieldID: number, fieldValue: string, fieldType: string) {
    return this.http
      .post('/Checklist/UpdateHeaderFieldValue', {
        checklistInstanceID: checklistInstanceID,
        checklistHeaderFieldID: checklistHeaderFieldID,
        fieldValue: fieldValue,
        fieldType: fieldType,
      })
      .toPromise();
  }

  submitChecklist(checklistInstanceID: number, pdfData: string = null, pdfFileName: string = null) {
    return this.http
      .post('/Checklist/SubmitChecklist', {
        checklistInstanceID: checklistInstanceID,
        pdfData: pdfData,
        pdfFileName: pdfFileName,
      })
      .toPromise();
  }

  deleteChecklistItem(checklistInstanceID: number, mode: string) {
    let url: string;
    if (mode === 'C') {
      url = '/Checklist/DeleteChecklist';
    } else if (mode === 'H') {
      url = '/Checklist/DeleteActionItem';
    } else {
      throw new Error('Invalid Checklist Mode');
    }
    return this.http
      .post(url, {
        checklistInstanceID: checklistInstanceID,
      })
      .toPromise();
  }

  createNewChecklist(checklistTemplateID: number, mode: string) {
    let url: string;
    if (mode === 'C') {
      url = '/Checklist/CreateNewChecklist';
    } else if (mode === 'H') {
      url = '/Checklist/CreateNewActionItem';
    } else {
      throw new Error('Invalid Checklist Mode');
    }

    return this.http.post(url, { checklistTemplateID: checklistTemplateID }).toPromise();
  }

  getTypeSign(type: string, name: string = null) {
    let url = '/Checklist/GetTypeSign?type=' + type;
    if (name != null) url += '&name=' + name;
    return this.http.get(url).toPromise();
  }

  sendChecklistsToVista(
    checklistInstanceID: number,
    checklistTemplateID: number,
    attachDocumentFlagValue: number,
    createdBy: number,
    attachmentType: number,
    fileName?: string
  ) {
    return this.http
      .post('/Checklist/SendChecklistsToVista', {
        checklistInstanceID: checklistInstanceID,
        checklistTemplateID: checklistTemplateID,
        attachDocumentFlagValue: attachDocumentFlagValue,
        createdBy: createdBy,
        attachmentType: attachmentType,
        fileName: fileName,
      })
      .toPromise();
  }

  getLookup() {
    const url = '/Checklist/GetLookup';
    return this.http.get(url).toPromise();
  }

  GetLookupChoices(checklistLookupID: number) {
    const url = '/Checklist/GetLookupChoices?checklistLookupID=' + checklistLookupID;
    return this.http.get(url).toPromise();
  }

  updateLookupChoices(
    checklistHeaderChoiceID: number,
    checklistLookupID: number,
    choiceShortName: string,
    choiceLongName: string,
    choiceIsActive: boolean,
    isValueNotUnique: boolean
  ) {
    return this.http
      .post('/Checklist/UpdateLookupChoices', {
        checklistHeaderChoiceID: checklistHeaderChoiceID,
        checklistLookupID: checklistLookupID,
        choiceShortName: choiceShortName,
        choiceLongName: choiceLongName,
        choiceIsActive: choiceIsActive,
        isValueNotUnique: isValueNotUnique,
      })
      .toPromise();
  }

  deleteLookupChoices(checklistHeaderChoiceID: number, checklistLookupID: number) {
    return this.http
      .post('/Checklist/DeleteLookupChoices', {
        checklistHeaderChoiceID: checklistHeaderChoiceID,
        checklistLookupID: checklistLookupID,
      })
      .toPromise();
  }

  moveSeqLookupChoices(direction: number, checklistHeaderChoiceID: number) {
    return this.http
      .post('/Checklist/MoveSeqLookupChoices', {
        direction: direction,
        checklistHeaderChoiceID: checklistHeaderChoiceID,
      })
      .toPromise();
  }

  updateLookup(
    checklistLookupID: number,
    checklistLookupName: string,
    checklistLookupSQLOverride: string,
    checklistLookupIsActive: boolean
  ) {
    return this.http
      .post('/Checklist/UpdateLookup', {
        checklistLookupID: checklistLookupID,
        checklistLookupName: checklistLookupName,
        checklistLookupSQLOverride: checklistLookupSQLOverride,
        checklistLookupIsActive: checklistLookupIsActive,
      })
      .toPromise();
  }

  evaluateLookup(checklistLookupID: number, includeInactive: boolean): Promise<CHLookupEvaluate_Result[]> {
    const url = '/Checklist/EvaluateLookup';
    const httpParams = new HttpParams().set('checklistLookupID', checklistLookupID).set('includeInactive', includeInactive);
    return this.http.get<CHLookupEvaluate_Result[]>(url, { params: httpParams }).toPromise();
  }

  updateItem(
    checklistInstanceItemID: any,
    itemComments: any,
    itemResponse: any,
    itemIsCorrected: any,
    correctedComments: any,
    signatureDataStr: any,
    typeSign: any,
    typeID: any,
    latitude: any,
    longitude: any,
    itemResponseDisplay: any
  ) {
    return this.http
      .post('/Checklist/UpdateItem', {
        checklistInstanceItemID: checklistInstanceItemID,
        itemComments: itemComments,
        itemResponse: itemResponse,
        itemIsCorrected: itemIsCorrected,
        correctedComments: correctedComments,
        signatureDataStr: signatureDataStr,
        typeSign: typeSign,
        typeID: typeID,
        latitude: latitude,
        longitude: longitude,
        itemResponseDisplay: itemResponseDisplay,
      })
      .toPromise();
  }

  getChoicesByTemplateItemID(checklistTemplateItemID: any, checklistInstanceID: any) {
    const url =
      '/CheckList/GetChoicesByTemplateItemID?checklistTemplateItemID=' +
      checklistTemplateItemID +
      '&checklistInstanceID=' +
      checklistInstanceID;
    return this.http.get(url).toPromise();
  }
  getChecklistInstancePDF(checklistInstanceID: number) {
    const url = '/Checklist/GetChecklistInstancePDF?checklistInstanceID=' + checklistInstanceID;
    return this.http.get(url).toPromise();
  }

  copyChecklistInstance(
    fromChecklistInstanceID: number,
    toChecklistInstanceID: number,
    latitude?: number,
    longitude?: number
  ): Promise<ApiResponse> {
    const url = '/Checklist/CopyChecklistInstance';
    return this.http
      .post<ApiResponse>(url, {
        fromChecklistInstanceID: fromChecklistInstanceID,
        toChecklistInstanceID: toChecklistInstanceID,
        longitude: longitude,
        latitude: latitude,
      })
      .toPromise();
  }
}
